/**
 * Cache LRU in memoria per le immagini private.
 *
 * Le immagini private vengono scaricate dal BE che, su alcuni clienti, inoltra
 * la richiesta a un servizio esterno a costo per chiamata. Mantenere in memoria
 * un numero limitato di object URL evita di riscaricare la stessa immagine ad
 * ogni render.
 *
 * La cache "possiede" gli object URL: vengono revocati (URL.revokeObjectURL)
 * solo quando l'entry viene rimossa per eviction LRU o svuotamento. I chiamanti
 * NON devono revocare gli URL restituiti.
 */

const DEFAULT_MAX_ENTRIES = 50

const parseMaxEntries = (): number => {
  const raw = process.env.REACT_APP_PRIVATE_IMAGE_CACHE_SIZE
  const parsed = raw ? parseInt(raw, 10) : NaN
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_MAX_ENTRIES
}

type Fetcher = () => Promise<string>

export class PrivateImageCache {
  // Map mantiene l'ordine di inserimento: la prima chiave è la meno usata di recente (LRU).
  private readonly cache = new Map<string, string>()
  // Richieste in corso, per deduplicare fetch concorrenti sulla stessa chiave.
  private readonly inflight = new Map<string, Promise<string>>()
  private readonly maxEntries: number

  constructor(maxEntries: number) {
    this.maxEntries = maxEntries
  }

  static buildKey(imageId: string, type: string): string {
    return `${imageId}::${type}`
  }

  private touch(key: string, url: string): void {
    // Re-inserendo la chiave la si sposta in fondo alla Map (most-recently-used).
    this.cache.delete(key)
    this.cache.set(key, url)
  }

  private store(key: string, url: string): void {
    this.cache.delete(key)
    this.cache.set(key, url)
    while (this.cache.size > this.maxEntries) {
      const oldestKey = this.cache.keys().next().value as string | undefined
      if (oldestKey === undefined) break
      const oldestUrl = this.cache.get(oldestKey)
      this.cache.delete(oldestKey)
      if (oldestUrl) URL.revokeObjectURL(oldestUrl)
    }
  }

  /**
   * Restituisce l'object URL dell'immagine, riusando la cache o la richiesta in
   * corso quando possibile. `fetcher` viene invocato solo in caso di cache miss.
   * Le stringhe vuote (fetch fallita) non vengono memorizzate.
   */
  async resolve(key: string, fetcher: Fetcher): Promise<string> {
    const cached = this.cache.get(key)
    if (cached) {
      this.touch(key, cached)
      return cached
    }

    const pending = this.inflight.get(key)
    if (pending) return pending

    const request = (async () => {
      const url = await fetcher()
      if (url) this.store(key, url)
      return url
    })()

    this.inflight.set(key, request)
    try {
      return await request
    } finally {
      this.inflight.delete(key)
    }
  }

  /** Svuota la cache revocando tutti gli object URL (es. al logout). */
  clear(): void {
    this.cache.forEach((url) => {
      if (url) URL.revokeObjectURL(url)
    })
    this.cache.clear()
    this.inflight.clear()
  }
}

export const privateImageCache = new PrivateImageCache(parseMaxEntries())
